/**
 * TripodEntity - 삼발이 도형 Entity
 */
import type { IGroup, ILine, IRenderer, IShape } from '../../../backend/IRenderer'
import { SHAPES } from '../../../const'
import type { ShapeCreateConfigs, Vector2d } from '../../../types'
import type { TripodShapePoints } from '@/app/data/type'

/**
 * Tripod 생성 설정
 */
export interface TripodCreateConfig {
  color: string
  tripodShapePoints?: TripodShapePoints
}

/**
 * Tripod 초기화 설정
 */
export interface TripodInitConfig {
  id?: string
  createPoint: Vector2d
  balloonPoint?: Vector2d
  config: TripodCreateConfig
  rotation?: number
  layerId?: string
  balloonFontColor?: string
  balloonFillColor?: string
  balloonScale?: number
  connectedLineThicknessScale?: number
  balloonFontScale?: number
  pointShapeScale?: number
  areaBorderThicknessScale?: number
  interactionMode?: boolean
}

/**
 * 화살표헤드 방향 계산
 * @param dot - startPoint 절대좌표 [x, y]
 * @param tripodData - tripod 도형 데이터
 * @param connectionEnd - dot을 주축선에 투영한 연결점 (절대좌표)
 * @param minLength - 화살표헤드-선 최소길이 임계 (0 이하면 임계 비활성)
 * @returns 정규화된 방향 벡터. 수직거리가 임계 미만이면 주축선 축의 바깥 방향을 반환
 */
export function computeTripodArrowDirection(
  dot: number[],
  tripodData: TripodShapePoints,
  connectionEnd: { x: number; y: number },
  minLength: number,
): { x: number; y: number } {
  const vx = dot[0] - connectionEnd.x
  const vy = dot[1] - connectionEnd.y
  const length = Math.hypot(vx, vy)

  if (minLength <= 0 || length >= minLength) {
    return length > 0 ? { x: vx / length, y: vy / length } : { x: 0, y: -1 }
  }

  // 임계 안쪽: 주축선 축 방향의 바깥쪽(± 단위벡터)을 가리킴
  const dx = tripodData.mainAxisPoint2[0] - tripodData.mainAxisPoint1[0]
  const dy = tripodData.mainAxisPoint2[1] - tripodData.mainAxisPoint1[1]
  const axisLength = Math.hypot(dx, dy)
  const axis = axisLength > 0 ? { x: dx / axisLength, y: dy / axisLength } : { x: 1, y: 0 }
  const centerX = (tripodData.mainAxisPoint1[0] + tripodData.mainAxisPoint2[0]) / 2
  const centerY = (tripodData.mainAxisPoint1[1] + tripodData.mainAxisPoint2[1]) / 2
  const side = (dot[0] - centerX) * axis.x + (dot[1] - centerY) * axis.y
  return side >= 0 ? axis : { x: -axis.x, y: -axis.y }
}

/**
 * Tripod 도형 생성 함수
 * @param initConfig - 초기화 설정
 * @param createPoint - 도형 생성 기준점
 * @param lineWidth - 선 굵기
 * @param pointShapeScale - 점 크기 스케일
 * @param arrowMinLength - 화살표헤드-선 최소길이 임계 (0 이하면 임계 비활성)
 * @returns Tripod 관련 노드 핸들들
 */
export function createTripodShape(
  renderer: IRenderer,
  initConfig: TripodInitConfig,
  createPoint: Vector2d,
  lineWidth: number,
  pointShapeScale: number,
  arrowMinLength: number = 0,
): {
  mainAxisLine: ILine
  tripodGroup: IGroup
  tripodTransformGroup: IGroup
  pointData: TripodShapePoints
} {
  const tripodConfig = initConfig.config as ShapeCreateConfigs[typeof SHAPES.TRIPOD]
  const tripodData = tripodConfig.tripodShapePoints

  // 전체 TRIPOD 그룹 생성
  const tripodGroup = renderer.createGroup({ x: 0, y: 0 })
  // transformer 대상 그룹 생성 (주축선 + 시작점들 + 연결선들 + balloon 포함)
  const tripodTransformGroup = renderer.createGroup({ x: 0, y: 0, name: 'tripodTransformGroup' })

  // 1. 주축선 그리기 (mainAxisPoint1 -> mainAxisPoint2)
  const mainAxisLine = renderer.createLine({
    points: [
      tripodData.mainAxisPoint1[0] - createPoint.x,
      tripodData.mainAxisPoint1[1] - createPoint.y,
      tripodData.mainAxisPoint2[0] - createPoint.x,
      tripodData.mainAxisPoint2[1] - createPoint.y,
    ],
    stroke: tripodConfig.color,
    strokeWidth: lineWidth,
    lineCap: 'round',
    name: 'mainAxis', // 주축선 식별용 name 설정
    hitArea: { kind: 'strokeWidth', px: 30 }, // 넓은 hit 영역
    listening: true, // 클릭 이벤트 수신
  })
  tripodTransformGroup.add(mainAxisLine)

  // 2. 시작점들(dots)과 연결선들 그리기
  const currentTailStyle = tripodData.tailStyle || 'dot' // tailStyle 확인
  tripodData.dots.forEach(dot => {
    // 시작점에 POINT shape과 동일한 크기로 원 그리기
    const pointRadius = initConfig.pointShapeScale ?? pointShapeScale
    const mainAxisHitWidth = 30 // 주축선의 hitStrokeWidth
    const startPointHitWidth = Math.round(mainAxisHitWidth * 1.2) // 주축선의 1.2배

    // 시작점 hit 영역용 투명한 큰 원 생성 (전체 영역 클릭 가능)
    // hitRadius는 주축선 hit width의 1.2배가 되도록 설정 (반지름 기준)
    const hitRadius = Math.max(pointRadius + 6, startPointHitWidth / 2 + 2) // 최소 20px
    // tailStyle에 따라 다른 시각적 요소 생성
    let visualElement: IShape

    if (currentTailStyle === 'arrow') {
      // 화살표 방향 계산 (주축선 반대편, 최소길이 임계 안쪽이면 주축선 축의 바깥 방향)
      const startPoint = { x: dot[0], y: dot[1] }
      const connectionEnd = __projectPointOntoMainAxisForCreate(startPoint, tripodData)
      const arrowDirection = computeTripodArrowDirection(
        dot,
        tripodData,
        connectionEnd,
        arrowMinLength,
      )

      // 삼각형 크기 설정
      const triangleSize = pointRadius * 1.2

      // 정삼각형 생성 (startPoint를 중심으로)
      const centerX = dot[0] - createPoint.x
      const centerY = dot[1] - createPoint.y

      // 정삼각형의 한 변의 길이
      const sideLength = triangleSize * 1.4

      // 화살표 방향을 기준으로 정삼각형 생성
      // 삼각형의 무게중심을 startPoint 위치로 설정
      const height = (sideLength * Math.sqrt(3)) / 2 // 정삼각형의 높이
      const centroidToVertex = (2 * height) / 3 // 무게중심에서 꼭짓점까지의 거리
      const centroidToBase = height / 3 // 무게중심에서 밑변까지의 거리

      // 정삼각형의 3개 꼭짓점 계산
      const tip = {
        x: centerX + arrowDirection.x * centroidToVertex,
        y: centerY + arrowDirection.y * centroidToVertex,
      }

      // 밑변의 두 점 (화살표 방향에 수직인 방향으로 배치)
      const perpendicular = { x: -arrowDirection.y, y: arrowDirection.x }
      const base1 = {
        x: centerX - arrowDirection.x * centroidToBase + perpendicular.x * (sideLength / 2),
        y: centerY - arrowDirection.y * centroidToBase + perpendicular.y * (sideLength / 2),
      }
      const base2 = {
        x: centerX - arrowDirection.x * centroidToBase - perpendicular.x * (sideLength / 2),
        y: centerY - arrowDirection.y * centroidToBase - perpendicular.y * (sideLength / 2),
      }

      // 삼각형 Line 생성 (닫힌 형태, 좌표 시스템 수정)
      visualElement = renderer.createLine({
        x: centerX, // hitCircle과 동일한 x 위치
        y: centerY, // hitCircle과 동일한 y 위치
        points: [
          tip.x - centerX,
          tip.y - centerY, // 삼각형 끝점 (상대좌표)
          base1.x - centerX,
          base1.y - centerY, // 밑변 점1 (상대좌표)
          base2.x - centerX,
          base2.y - centerY, // 밑변 점2 (상대좌표)
          tip.x - centerX,
          tip.y - centerY, // 다시 끝점으로 (닫기)
        ],
        fill: tripodConfig.color,
        stroke: tripodConfig.color,
        strokeWidth: 1,
        closed: true,
        listening: false,
        name: 'startPointVisual',
        perfectDraw: false,
      })
    } else {
      // 기본 원형 (dot)
      visualElement = renderer.createEllipse({
        x: dot[0] - createPoint.x,
        y: dot[1] - createPoint.y,
        radiusX: pointRadius,
        radiusY: pointRadius,
        fill: tripodConfig.color,
        stroke: tripodConfig.color,
        strokeWidth: 1,
        listening: false,
        name: 'startPointVisual',
        perfectDraw: false,
      })
    }
    tripodTransformGroup.add(visualElement)

    // 시작점에서 주축선으로 수직 연결선 그리기
    const startPoint = { x: dot[0], y: dot[1] }
    const connectionEnd = __projectPointOntoMainAxisForCreate(startPoint, tripodData)

    const connectionLine = renderer.createLine({
      points: [
        startPoint.x - createPoint.x,
        startPoint.y - createPoint.y,
        connectionEnd.x - createPoint.x,
        connectionEnd.y - createPoint.y,
      ],
      stroke: tripodConfig.color,
      strokeWidth: lineWidth,
      lineCap: 'round',
      name: 'connectionLine', // 연결선 식별용 name 설정
      hitArea: { kind: 'strokeWidth', px: 20 }, // 넓은 hit 영역 (클릭 선택용 — 드래그 조작은 없음)
      listening: true, // 클릭 이벤트 수신
    })
    tripodTransformGroup.add(connectionLine)

    // 그 위에 투명한 hit 영역 원을 생성 (더 큰 크기로 위에 배치)
    const hitCircle = renderer.createEllipse({
      x: dot[0] - createPoint.x,
      y: dot[1] - createPoint.y,
      radiusX: hitRadius,
      radiusY: hitRadius,
      fill: tripodConfig.color, // 실제 색상으로 fill
      stroke: tripodConfig.color,
      strokeWidth: 1,
      listening: true,
      name: 'startPoint', // 시작점 식별용
      opacity: 0,
      perfectDraw: false,
    })
    tripodTransformGroup.add(hitCircle) // 나중에 add해서 위쪽 레이어에 배치
  })

  // transformer 대상 그룹을 전체 그룹에 추가
  tripodGroup.add(tripodTransformGroup)

  return {
    mainAxisLine,
    tripodGroup,
    tripodTransformGroup,
    pointData: tripodData,
  }
}
/**
 * TRIPOD 회전축 최적화를 위한 투명한 가상 요소 추가
 * 시작점들의 반대편(주축선 너머)에 배치하여 바운더리를 정사각형으로 조정
 */
/**
 * 점을 주축선에 수직으로 투영하여 연결점 찾기 (TRIPOD 생성용)
 */
export function __projectPointOntoMainAxisForCreate(
  point: { x: number; y: number },
  tripodData: TripodShapePoints,
): { x: number; y: number } {
  const mainAxisStart = {
    x: tripodData.mainAxisPoint1[0],
    y: tripodData.mainAxisPoint1[1],
  }
  const mainAxisEnd = {
    x: tripodData.mainAxisPoint2[0],
    y: tripodData.mainAxisPoint2[1],
  }

  // 주축선 벡터
  const mainAxisVector = {
    x: mainAxisEnd.x - mainAxisStart.x,
    y: mainAxisEnd.y - mainAxisStart.y,
  }

  // 시작점에서 주축선 시작점까지의 벡터
  const pointToAxisStart = {
    x: point.x - mainAxisStart.x,
    y: point.y - mainAxisStart.y,
  }

  // 주축선 벡터의 길이 제곱
  const mainAxisLengthSquared =
    mainAxisVector.x * mainAxisVector.x + mainAxisVector.y * mainAxisVector.y

  if (mainAxisLengthSquared === 0) {
    // 주축선 길이가 0인 경우 시작점 반환
    return mainAxisStart
  }

  // 투영 계수 계산 (0~1 범위로 클램핑)
  const projectionRatio = Math.max(
    0,
    Math.min(
      1,
      (pointToAxisStart.x * mainAxisVector.x + pointToAxisStart.y * mainAxisVector.y) /
        mainAxisLengthSquared,
    ),
  )

  // 투영점 계산
  return {
    x: mainAxisStart.x + mainAxisVector.x * projectionRatio,
    y: mainAxisStart.y + mainAxisVector.y * projectionRatio,
  }
}
/**
 * Tripod Entity export 데이터
 */
export interface TripodExportData {
  type: typeof SHAPES.TRIPOD
  shapeId: string
  point: Vector2d
  balloonPoint: Vector2d
  color: string
  layerId: string
  tripodShapePoints?: TripodShapePoints
  rotate?: number
  balloonFontColor?: string
  balloonFillColor?: string
  balloonScale?: number
  connectedLineThicknessScale?: number
  balloonFontScale?: number
  pointShapeScale?: number
  areaBorderThicknessScale?: number
}

/**
 * Tripod Entity를 export 데이터로 변환
 */
export function exportTripodData(entity: {
  id: string
  position: Vector2d
  balloonPosition: Vector2d
  color: string
  layerId: string
  tripodShapePoints?: TripodShapePoints
  rotation?: number
  balloonFontColor?: string
  balloonFillColor?: string
  balloonScale?: number
  connectedLineThicknessScale?: number
  balloonFontScale?: number
  pointShapeScale?: number
  areaBorderThicknessScale?: number
}): TripodExportData {
  return {
    type: SHAPES.TRIPOD,
    shapeId: entity.id,
    point: entity.position,
    balloonPoint: entity.balloonPosition,
    color: entity.color,
    layerId: entity.layerId,
    tripodShapePoints: entity.tripodShapePoints,
    rotate: entity.rotation,
    balloonFontColor: entity.balloonFontColor,
    balloonFillColor: entity.balloonFillColor,
    balloonScale: entity.balloonScale,
    connectedLineThicknessScale: entity.connectedLineThicknessScale,
    balloonFontScale: entity.balloonFontScale,
    pointShapeScale: entity.pointShapeScale,
    areaBorderThicknessScale: entity.areaBorderThicknessScale,
  }
}
